/**
 * Cart Companion Firestore service.
 *
 * Vault items are stored at: userContent/{uid}/cartVault/{itemId}
 *
 * Each item has a user-chosen cooling-off period. After expiry the UI treats
 * the item as "ready" — the user can then decide to buy or dismiss.
 */

import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  updateDoc,
  getDoc,
  getDocs,
  setDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  increment,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { recordFeatureUseEvent } from '@shared/services/analyticsService';
import { isPlaceholderTitle } from '../utils/urlFirstProductPreview';
import { logError } from '@/lib/log';

// ---- Types ----

export type VaultItemStatus = 'draft' | 'waiting' | 'ready' | 'purchased' | 'dismissed';

/** Background metadata enrichment lifecycle (Phase 1 async). */
export type MetadataEnrichmentStatus = 'pending' | 'complete' | 'failed';

export type SecondLookStatus = 'researching' | 'complete' | 'failed';

/** Panel 1 — regret-shaped patterns mined from scraped product reviews. */
export interface SecondLookOwners {
  reviewCount: number | null;
  averageRating: number | null;
  concerns: { concern: string; mentions: number | null }[];
  praise: string | null;
  wishKnown: string | null;
  source: 'product_page' | 'none';
}

/** Panel 2 — the user's own reflection history (deterministic, 60-day window). */
export interface SecondLookPattern {
  merchantName: string | null;
  merchantReflectedCount: number;
  merchantRegretCount: number;
  merchantRegretTotal: number;
  categoryName: string | null;
  categoryRegretCount: number;
  windowDays: number;
}

/** Panel 3 — the price relative to the user's last-30-day spend. */
export interface SecondLookPriceContext {
  price: number | null;
  monthlySpend: number | null;
  pctOfMonthlySpend: number | null;
}

export interface SecondLookData {
  status: SecondLookStatus;
  requestedAt: string | null;
  completedAt: string | null;
  owners?: SecondLookOwners;
  /** Absent when the user has no transaction data — the UI hides the panel. */
  pattern?: SecondLookPattern;
  priceContext?: SecondLookPriceContext;
  error?: string;
}

export interface CartVaultItem {
  id: string;
  title: string;
  imageUrl: string | null;
  price: number | null;
  currency: string;
  url: string;
  retailer: string;
  asin: string | null;
  status: VaultItemStatus;
  coolingOffDays: number;
  addedAt: string;   // UTC ISO — when item was first added to vault
  activatedAt: string | null;  // UTC ISO — when cooling-off started (null for direct adds)
  expiresAt: string;  // UTC ISO — empty string for drafts
  purchasedAt: string | null;
  dismissedAt: string | null;
  /** Price at time of dismiss — tracks "money not spent" */
  savedAmount: number | null;
  /** Second Look brief (paid tier only) */
  secondLook?: SecondLookData;
  /** Server-side full enrichment after URL-first add */
  metadataEnrichment?: MetadataEnrichmentStatus;
}

export interface CartCompanionStats {
  totalSaved: number;
  totalPurchased: number;
  itemsDismissed: number;
  itemsPurchased: number;
}

export interface CartVaultItemWrite {
  title: string;
  imageUrl: string | null;
  price: number | null;
  currency: string;
  url: string;
  retailer: string;
  asin: string | null;
  status: VaultItemStatus;
  coolingOffDays: number;
  addedAt: Timestamp;
  activatedAt: Timestamp;
  expiresAt: Timestamp;
}

// ---- Helpers ----

function vaultCollection(uid: string) {
  return collection(db, 'userContent', uid, 'cartVault');
}

// ---- Service functions ----

/** Check if the user already has a vault item with this URL (any status except dismissed/purchased). */
export async function findVaultItemByUrl(uid: string, url: string): Promise<CartVaultItem | null> {
  const q = query(vaultCollection(uid), where('url', '==', url));
  const snapshot = await getDocs(q);

  for (const d of snapshot.docs) {
    const data = d.data();
    const status = data.status ?? 'waiting';
    // Only return active items (draft or waiting) — not already resolved
    if (status === 'draft' || status === 'waiting') {
      return {
        id: d.id,
        title: data.title ?? '',
        imageUrl: data.imageUrl ?? null,
        price: data.price ?? null,
        currency: data.currency ?? 'USD',
        url: data.url ?? '',
        retailer: data.retailer ?? '',
        asin: data.asin ?? null,
        status,
        coolingOffDays: data.coolingOffDays ?? 0,
        addedAt: data.addedAt?.toDate?.().toISOString() ?? new Date().toISOString(),
        activatedAt: data.activatedAt?.toDate?.().toISOString() ?? null,
        expiresAt: data.expiresAt?.toDate?.().toISOString() ?? '',
        purchasedAt: null,
        dismissedAt: null,
        savedAmount: null,
      };
    }
  }
  return null;
}

/** Save an item as a draft (no duration set yet). */
export async function addDraftItem(
  uid: string,
  item: { title: string; imageUrl: string | null; price: number | null; currency: string; url: string; retailer: string; asin: string | null },
  options?: { queueAsyncEnrichment?: boolean },
): Promise<string> {
  const now = new Date();
  const data: Record<string, unknown> = {
    title: item.title,
    imageUrl: item.imageUrl,
    price: item.price,
    currency: item.currency,
    url: item.url,
    retailer: item.retailer,
    asin: item.asin,
    status: 'draft',
    coolingOffDays: 0,
    addedAt: Timestamp.fromDate(now),
  };
  if (options?.queueAsyncEnrichment) {
    data.metadataEnrichment = 'pending';
  }

  const ref = await addDoc(vaultCollection(uid), data);
  return ref.id;
}

/** Activate a draft item by setting its cooling-off period. */
export async function activateDraftItem(
  uid: string,
  itemId: string,
  coolingOffDays: number,
): Promise<void> {
  const now = new Date();
  const expires = new Date(now.getTime() + coolingOffDays * 24 * 60 * 60 * 1000);

  await updateDoc(doc(db, 'userContent', uid, 'cartVault', itemId), {
    status: 'waiting',
    coolingOffDays,
    activatedAt: Timestamp.fromDate(now),
    expiresAt: Timestamp.fromDate(expires),
  });
}

/** Add an item to the user's cart vault with a configurable cooldown window. */
type VaultItemInput = Omit<
  CartVaultItem,
  'id' | 'addedAt' | 'expiresAt' | 'status' | 'purchasedAt' | 'dismissedAt' | 'activatedAt' | 'savedAmount' | 'secondLook' | 'metadataEnrichment'
>;

export async function addVaultItem(
  uid: string,
  item: VaultItemInput,
  coolingOffDays: number,
): Promise<string> {
  const now = new Date();
  const expires = new Date(now.getTime() + coolingOffDays * 24 * 60 * 60 * 1000);

  const data: CartVaultItemWrite = {
    title: item.title,
    imageUrl: item.imageUrl,
    price: item.price,
    currency: item.currency,
    url: item.url,
    retailer: item.retailer,
    asin: item.asin,
    status: 'waiting',
    coolingOffDays,
    addedAt: Timestamp.fromDate(now),
    activatedAt: Timestamp.fromDate(now),
    expiresAt: Timestamp.fromDate(expires),
  };

  const ref = await addDoc(vaultCollection(uid), data);
  recordFeatureUseEvent(uid, 'cart_companion', 'add', { oncePerSession: false }).catch(() => {});
  return ref.id;
}

/**
 * Create a vault item from a share-extension hand-off where the user already chose the
 * cooling-off duration (iOS "mini-app" flow). The hold timer counts from `sharedAtISO`
 * (when the user shared), not when the app later opened. Title/image are filled in by
 * the server enrichment trigger (metadataEnrichment: 'pending').
 */
export async function addSharedVaultItem(
  uid: string,
  item: {
    title: string;
    price: number | null;
    url: string;
    retailer: string;
    asin: string | null;
    coolingOffDays: number;
    sharedAtISO: string;
  },
): Promise<string> {
  const sharedAt = new Date(item.sharedAtISO);
  const validSharedAt = isNaN(sharedAt.getTime()) ? new Date() : sharedAt;
  const expires = new Date(validSharedAt.getTime() + item.coolingOffDays * 24 * 60 * 60 * 1000);

  const data: Record<string, unknown> = {
    title: item.title,
    imageUrl: null,
    price: item.price,
    currency: 'USD',
    url: item.url,
    retailer: item.retailer,
    asin: item.asin,
    status: 'waiting',
    coolingOffDays: item.coolingOffDays,
    addedAt: Timestamp.fromDate(validSharedAt),
    activatedAt: Timestamp.fromDate(validSharedAt),
    expiresAt: Timestamp.fromDate(expires),
    metadataEnrichment: 'pending',
  };

  const ref = await addDoc(vaultCollection(uid), data);
  recordFeatureUseEvent(uid, 'cart_companion', 'add', { oncePerSession: false }).catch(() => {});
  return ref.id;
}

/** Update the status of a vault item (purchased or dismissed).
 *  On dismiss: records savedAmount and updates cumulative stats.
 *  On purchase: updates cumulative stats.
 *  Returns the savedAmount (for dismiss confirmation UI). */
export async function updateVaultItemStatus(
  uid: string,
  itemId: string,
  status: 'purchased' | 'dismissed',
): Promise<{ savedAmount: number | null }> {
  const itemRef = doc(db, 'userContent', uid, 'cartVault', itemId);
  const timestampField = status === 'purchased' ? 'purchasedAt' : 'dismissedAt';

  // Read the item to get its price for stats
  const itemDoc = await getDoc(itemRef);
  const itemData = itemDoc.data();
  const price = itemData?.price ?? null;

  const updateData: Record<string, any> = {
    status,
    [timestampField]: Timestamp.now(),
  };

  if (status === 'dismissed' && price != null) {
    updateData.savedAmount = price;
  }

  await updateDoc(itemRef, updateData);

  // Update cumulative stats on userContent/{uid}.
  // NOTE: must be a nested map — setDoc(merge) treats dotted keys as LITERAL
  // field names (unlike updateDoc), which is how the previous version wrote
  // "cartCompanionStats.totalSaved" as a top-level field the reader never saw.
  const statsRef = doc(db, 'userContent', uid);
  const statsUpdate: Record<string, any> =
    status === 'dismissed'
      ? {
          cartCompanionStats: {
            itemsDismissed: increment(1),
            ...(price != null ? { totalSaved: increment(price) } : {}),
          },
        }
      : {
          cartCompanionStats: {
            itemsPurchased: increment(1),
            ...(price != null ? { totalPurchased: increment(price) } : {}),
          },
        };

  try {
    await setDoc(statsRef, statsUpdate, { merge: true });
  } catch (err) {
    logError(err, { context: 'cart_companion_update_stats' });
  }

  const action = status === 'dismissed' ? 'let_go' : 'purchase';
  recordFeatureUseEvent(uid, 'cart_companion', action, { oncePerSession: false }).catch(() => {});

  return { savedAmount: status === 'dismissed' ? price : null };
}

/** Update editable fields on a vault item (title, price, currency). */
export async function updateVaultItem(
  uid: string,
  itemId: string,
  fields: {
    title?: string;
    price?: number | null;
    currency?: string;
    imageUrl?: string | null;
    url?: string;
    retailer?: string;
    metadataEnrichment?: MetadataEnrichmentStatus;
  },
): Promise<void> {
  const ref = doc(db, 'userContent', uid, 'cartVault', itemId);
  await updateDoc(ref, { ...fields });
}

/**
 * Merge enrichment results without overwriting user-edited title.
 * Keep merge rules aligned with `_merge_enrichment_into_vault` in product_metadata.py.
 */
export async function patchVaultFromEnrichment(
  uid: string,
  itemId: string,
  enrichment: {
    title?: string;
    imageUrl?: string | null;
    price?: number | null;
    currency?: string;
    url?: string;
    retailer?: string;
  },
  options?: { userTitle?: string },
): Promise<void> {
  const ref = doc(db, 'userContent', uid, 'cartVault', itemId);
  const snap = await getDoc(ref);
  const existing = snap.data();
  const userTitle = options?.userTitle?.trim() || existing?.title?.trim() || '';

  // Leave metadataEnrichment as 'pending' — server trigger marks 'complete' after full pass
  const updates: Record<string, unknown> = {};

  const enrichedTitle = enrichment.title?.trim();
  const itemUrl = (existing?.url as string | undefined) ?? '';
  const looksAutoGenerated = isPlaceholderTitle(
    userTitle,
    itemUrl,
    existing?.retailer as string | undefined,
  );

  if (enrichedTitle && (looksAutoGenerated || userTitle === enrichment.title)) {
    updates.title = enrichedTitle;
  }

  if (enrichment.imageUrl != null) updates.imageUrl = enrichment.imageUrl;
  if (enrichment.price != null && existing?.price == null) updates.price = enrichment.price;
  if (enrichment.currency) updates.currency = enrichment.currency;
  if (enrichment.url) updates.url = enrichment.url;
  if (enrichment.retailer) updates.retailer = enrichment.retailer;

  await updateDoc(ref, updates);
}

/** Update the Second Look enabled/disabled setting. */
export async function updateSecondLookSetting(uid: string, enabled: boolean): Promise<void> {
  const ref = doc(db, 'userContent', uid);
  await setDoc(ref, { cartCompanionSettings: { secondLookEnabled: enabled } }, { merge: true });
}

/** Revert an active item back to draft so the user can pick a new cooling-off duration. */
export async function revertToDraft(uid: string, itemId: string): Promise<void> {
  const ref = doc(db, 'userContent', uid, 'cartVault', itemId);
  await updateDoc(ref, {
    status: 'draft',
    coolingOffDays: 0,
    activatedAt: null,
    expiresAt: '',
  });
}

/** Remove an item from the vault (user removes an active item before expiry). */
export async function removeVaultItem(uid: string, itemId: string): Promise<void> {
  await deleteDoc(doc(db, 'userContent', uid, 'cartVault', itemId));
}

/** Subscribe to real-time vault updates. Returns an unsubscribe function. */
export function subscribeToVault(
  uid: string,
  onItems: (items: CartVaultItem[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(vaultCollection(uid), orderBy('addedAt', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: CartVaultItem[] = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          title: data.title ?? '',
          imageUrl: data.imageUrl ?? null,
          price: data.price ?? null,
          currency: data.currency ?? 'USD',
          url: data.url ?? '',
          retailer: data.retailer ?? 'amazon',
          asin: data.asin ?? null,
          status: data.status ?? 'waiting',
          coolingOffDays: data.coolingOffDays ?? 1,
          addedAt: data.addedAt?.toDate?.().toISOString() ?? new Date().toISOString(),
          activatedAt: data.activatedAt?.toDate?.().toISOString() ?? null,
          expiresAt: data.expiresAt?.toDate?.().toISOString() ?? '',
          purchasedAt: data.purchasedAt?.toDate?.().toISOString() ?? null,
          dismissedAt: data.dismissedAt?.toDate?.().toISOString() ?? null,
          savedAmount: data.savedAmount ?? null,
          secondLook: data.secondLook ? {
            status: data.secondLook.status,
            requestedAt: data.secondLook.requestedAt?.toDate?.().toISOString() ?? data.secondLook.requestedAt ?? null,
            completedAt: data.secondLook.completedAt?.toDate?.().toISOString() ?? data.secondLook.completedAt ?? null,
            owners: data.secondLook.owners ?? undefined,
            pattern: data.secondLook.pattern ?? undefined,
            priceContext: data.secondLook.priceContext ?? undefined,
            error: data.secondLook.error ?? undefined,
          } : undefined,
          metadataEnrichment: data.metadataEnrichment ?? undefined,
        };
      });
      onItems(items);
    },
    (error) => {
      logError(error, { context: 'cart_companion_vault_subscription' });
      onError?.(error);
    },
  );
}

/** Subscribe to cart companion stats and settings on userContent/{uid}. */
export function subscribeToCartCompanionStats(
  uid: string,
  onStats: (stats: CartCompanionStats) => void,
  onSecondLookEnabled?: (enabled: boolean) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'userContent', uid),
    (snapshot) => {
      const data = snapshot.data();
      const raw = data?.cartCompanionStats ?? {};
      // Legacy fallback: older clients wrote literal dotted field names via
      // setDoc(merge) — fold those into the totals so history isn't lost.
      const legacy = (field: string): number => data?.[`cartCompanionStats.${field}`] ?? 0;
      onStats({
        totalSaved: (raw.totalSaved ?? 0) + legacy('totalSaved'),
        totalPurchased: (raw.totalPurchased ?? 0) + legacy('totalPurchased'),
        itemsDismissed: (raw.itemsDismissed ?? 0) + legacy('itemsDismissed'),
        itemsPurchased: (raw.itemsPurchased ?? 0) + legacy('itemsPurchased'),
      });
      // Only the explicit Second Look toggle counts — the legacy Personal
      // Shopper flag is no longer honored (server-side trigger matches), so
      // legacy users aren't silently enrolled in paid research while the
      // Second Look UI is feature-flagged off.
      const settings = data?.cartCompanionSettings;
      onSecondLookEnabled?.(settings?.secondLookEnabled ?? false);
    },
    (error) => {
      logError(error, { context: 'cart_companion_stats_subscription' });
    },
  );
}
